import React, { useState, useEffect } from 'react';
import { Radio, RadioGroup, FormControl, FormControlLabel, CircularProgress, makeStyles } from '@material-ui/core';
import { Alert } from '@material-ui/lab';

import { ModalDialog } from '../Modal';
import { getBrand, updateBrand } from '../../stores/dataorch';

const useStyles = makeStyles((theme) => ({
  text: {
    color: '#4a4a4a',
    fontSize: '19px',
    margin: 0,
    fontFamily: 'AvenirNext-Medium',
  },
  subtitle: {
    fontSize: '13px',
    color: '#2a2444',
    fontFamily: 'AvenirNext-MediumItalic',
  },
  block: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    paddingTop: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  block__label: {
    textAlign: 'left',
    fontFamily: 'AvenirNext-DemiBold',
    fontSize: '13px',
    color: '#96979a',
    textTransform: 'uppercase',
    marginBottom: '-10px',
  },
  block__content: {
    color: '#96979a',
    fontSize: '14px',
    fontFamily: 'AvenirNext-Medium',
  },
  logo: {
    maxWidth: '100px',
    margin: '10px',
  },
  alert: {
    marginBottom: '4px',
  },
  loading: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '400px',
  },
}));

function BrandCustomize({ selectedBrandId, onOk, onClose }) {
  const classes = useStyles();
  const [formData, setFormData] = useState({});
  const [brand, setBrand] = useState(null);
  const [client, setClient] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  useEffect(() => {
    if (!selectedBrandId) return;

    setLoading(true);
    getBrand(selectedBrandId)
      .then((response) => {
        setLoading(false);
        setClient(response?.data?.client);
        setBrand(response?.data?.client_profile);

        const setting = response?.data?.client_profile;

        setFormData({
          is_use_profile_name: String(setting?.is_use_profile_name || false),
          is_use_profile_logo: String(setting?.is_use_profile_logo || false),
          is_use_profile_label: String(setting?.is_use_profile_label || false),
          is_use_profile_email: String(setting?.is_use_profile_email || false),
          is_use_profile_phone: String(setting?.is_use_profile_phone || false),
        });
      })
      .catch((error) => {
        setLoading(false);
        setError(error?.message || error.toString());
      });
  }, [selectedBrandId]);

  const handleSubmit = () => {
    const data = new FormData();
    data.append('id', selectedBrandId);
    data.append('is_use_profile_name', formData.is_use_profile_name === 'true');
    data.append('is_use_profile_email', formData.is_use_profile_email === 'true');
    data.append('is_use_profile_label', formData.is_use_profile_label === 'true');
    data.append('is_use_profile_logo', formData.is_use_profile_logo === 'true');
    data.append('is_use_profile_phone', formData.is_use_profile_phone === 'true');

    updateBrand(data)
      .then((response) => {
        if (response.status >= 200 && response.status < 300) return onOk('Brand was updated');

        if (response?.data?.message) setError(response?.data?.message);
      })
      .catch((error) => setError(error?.message || error.toString()));
  };

  return (
    <ModalDialog title="Customize Brand" open onOk={handleSubmit} onClose={onClose} okLabel="SAVE">
      <div>
        {error && (
          <Alert icon={false} severity="error" className={classes.alert} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        <section className={classes.block}>
          <span className={classes.text}>Tracking Page Settings</span>
          <span className={classes.subtitle}>Please select the information you want to be displayed on the Tracking page.</span>
        </section>
        {loading && (
          <div className={classes.loading}>
            <CircularProgress />
          </div>
        )}

        {(client?.company || brand?.name) && (
          <section className={classes.block}>
            <span className={classes.block__label}>Which company name to display?</span>
            <FormControl component="fieldset">
              <RadioGroup name="is_use_profile_name" value={formData.is_use_profile_name || false} onChange={handleChange} row>
                {client?.company && <FormControlLabel classes={{ label: classes.block__content }} value="false" control={<Radio color="primary" />} label={client?.company} />}
                {brand?.name && <FormControlLabel classes={{ label: classes.block__content }} value="true" control={<Radio color="primary" />} label={brand?.name} />}
              </RadioGroup>
            </FormControl>
          </section>
        )}

        {(client?.logo_url || brand?.logo) && (
          <section className={classes.block}>
            <span className={classes.block__label}>Which logo to display?</span>
            <FormControl component="fieldset">
              <RadioGroup name="is_use_profile_logo" value={formData.is_use_profile_logo || false} onChange={handleChange} row>
                {client?.logo_url && <FormControlLabel classes={{ label: classes.block__content }} value="false" control={<Radio color="primary" />} label={client?.logo_url && <img className={classes.logo} src={client?.logo_url} alt="" />} />}
                {brand?.logo && <FormControlLabel classes={{ label: classes.block__content }} value="true" control={<Radio color="primary" />} label={brand?.logo && <img className={classes.logo} src={brand?.logo} alt="" />} />}
              </RadioGroup>
            </FormControl>
          </section>
        )}

        {(client?.label || brand?.label) && (
          <section className={classes.block} style={{ display: 'none' }}>
            <span className={classes.block__label}>Which label design to display?</span>
            <FormControl component="fieldset">
              <RadioGroup name="is_use_profile_label" value={formData.is_use_profile_label || false} onChange={handleChange} row>
                {client?.label && <FormControlLabel classes={{ label: classes.block__content }} value="false" control={<Radio color="primary" />} label={client?.label} />}
                {brand?.label && <FormControlLabel classes={{ label: classes.block__content }} value="true" control={<Radio color="primary" />} label={brand?.label} />}
              </RadioGroup>
            </FormControl>
          </section>
        )}

        {(client?.email || brand?.email) && (
          <section className={classes.block}>
            <span className={classes.block__label}>Which support email address to display?</span>
            <FormControl component="fieldset">
              <RadioGroup name="is_use_profile_email" value={formData.is_use_profile_email || false} onChange={handleChange} row>
                {client?.email && <FormControlLabel classes={{ label: classes.block__content }} value="false" control={<Radio color="primary" />} label={client?.email} />}
                {brand?.email && <FormControlLabel classes={{ label: classes.block__content }} value="true" control={<Radio color="primary" />} label={brand?.email} />}
              </RadioGroup>
            </FormControl>
          </section>
        )}

        {(client?.phone_number || brand?.phone_number) && (
          <section className={classes.block}>
            <span className={classes.block__label}>Which support phone number to display?</span>
            <FormControl component="fieldset">
              <RadioGroup name="is_use_profile_phone" value={formData.is_use_profile_phone || false} onChange={handleChange} row>
                {client?.phone_number && <FormControlLabel classes={{ label: classes.block__content }} value="false" control={<Radio color="primary" />} label={client?.phone_number} />}
                {brand?.phone_number && <FormControlLabel classes={{ label: classes.block__content }} value="true" control={<Radio color="primary" />} label={brand?.phone_number} />}
              </RadioGroup>
            </FormControl>
          </section>
        )}
      </div>
    </ModalDialog>
  );
}

export default BrandCustomize;
